import {
  Form,
  FormAssertive,
  FormInput,
  FormSubmit,
  FormSwitch,
  useForm,
  validator,
} from "@/components/form"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { UseDialogFormProps, UseDialogProps } from "@/components/ui/hooks/useDialog"
import { resetAllStoresAndReload } from "@/store"
import { useIsSuperadmin } from "@/store/auth/hooks"
import { createMediasFolder } from "@/store/medias/actions"
import { match } from "ts-pattern"

/**
 * dictionary src/dictionaries/en/components/medias.json
 */
const dictionary = createContextMapper("components", "medias")

/**
 * CreateFolderDialog
 */
export const CreateFolderDialog: React.FC<UseDialogProps<string | null>> = ({ item, ...props }) => {
  const { _ } = useDictionary(dictionary("create-folder-dialog"))
  return (
    <Dialog {...props} title={_("title")} description={_("secondary")} className="sm:max-w-xl">
      {item !== false && <DialogForm {...props} item={item} />}
    </Dialog>
  )
}

/**
 * DialogForm
 */
const DialogForm: React.FC<UseDialogFormProps<string | null>> = ({
  item: parentId,
  onOpenChange,
}) => {
  const { _ } = useDictionary(dictionary("create-folder-dialog"))
  const _error = useErrorsDictionary()
  const _form = useFormDictionary()

  const isSuperadmin = useIsSuperadmin()

  const { min } = validator
  const form = useForm({
    allowSubmitAttempt: true,
    allowErrorSubmit: true,
    values: { name: "", lock: false },
    validate: validator({
      name: [min(1, _("name-required"))],
    }),
    onSubmit: async ({ values: { name, lock } }, event) => {
      event?.stopPropagation()
      if (!form.isValid) return _error("VALIDATION_FAILURE")
      const payload = { name, parentId, lock: isSuperadmin ? lock : undefined }
      return match(await createMediasFolder(payload))
        .with({ error: false }, () => {
          toast.success(_("success"))
          onOpenChange(false)
        })
        .otherwise(({ code }) =>
          match(code)
            .with("VALIDATION_FAILURE", _error)
            .with("INVALID_AUTH_SESSION", resetAllStoresAndReload)
            .otherwise(code => void toast.error(_error(code)))
        )
    },
  })

  return (
    <Form form={form} className="grid gap-6">
      <FormAssertive />
      <FormInput label={_("name-label")} name="name" placeholder={_("name-placeholder")} />
      {isSuperadmin && <FormSwitch label={_("lock-label")} name="lock" />}
      <Dialog.Footer className="sm:justify-start">
        <Dialog.Close asChild>
          <Button variant="secondary">{_form("cancel")}</Button>
        </Dialog.Close>
        <FormSubmit>{_form("create")}</FormSubmit>
      </Dialog.Footer>
    </Form>
  )
}
